import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageSyndication = () => {
  const [targets, setTargets] = useState([]);
  const [content, setContent] = useState([]);
  const [stats, setStats] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [targetsRes, contentRes, statsRes, logsRes, reportRes] = await Promise.all([
        axios.get(`${API}/syndication/targets`, { headers }),
        axios.get(`${API}/syndication/content`, { headers }),
        axios.get(`${API}/syndication/stats`, { headers }),
        axios.get(`${API}/syndication/logs`, { headers }),
        axios.get(`${API}/syndication/weekly-report`, { headers })
      ]);
      
      setTargets(targetsRes.data.targets || []);
      setContent(contentRes.data.content || []);
      setStats(statsRes.data);
      setLogs(logsRes.data.logs || []);
      setWeeklyReport(reportRes.data);
    } catch (error) {
      console.error('Failed to fetch syndication data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInitialize = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${API}/syndication/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      alert('Syndication system initialized!');
    } catch (error) {
      console.error('Failed to initialize:', error);
      alert('Failed to initialize syndication system');
    }
  };

  const handleGenerateContent = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const res = await axios.post(`${API}/syndication/content/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      alert(`Generated ${res.data.created} content pieces!`);
    } catch (error) {
      console.error('Failed to generate content:', error);
      alert('Failed to generate content');
    }
  };

  const handleLogSubmission = async (contentId, targetId, status) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${API}/syndication/log?content_id=${contentId}&target_id=${targetId}&status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      alert('Submission logged!');
    } catch (error) {
      console.error('Failed to log submission:', error);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      directory: 'bg-blue-100 text-blue-800',
      blog: 'bg-green-100 text-green-800',
      press: 'bg-purple-100 text-purple-800',
      social: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="p-6">Loading syndication data...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Content Syndication & Backlinks</h1>
          <p className="text-gray-500">Manage automated content distribution for SEO backlinks</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleGenerateContent} variant="outline">
            🔄 Generate Content
          </Button>
          <Button onClick={handleInitialize} className="bg-teal-600 hover:bg-teal-700">
            ⚡ Initialize System
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-gray-800">{stats.content?.total || 0}</div>
              <div className="text-sm text-gray-500">Total Content</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-yellow-600">{stats.content?.pending || 0}</div>
              <div className="text-sm text-gray-500">Pending</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{stats.content?.syndicated || 0}</div>
              <div className="text-sm text-gray-500">Syndicated</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{stats.targets?.active || 0}</div>
              <div className="text-sm text-gray-500">Active Targets</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-purple-600">{stats.submissions?.total || 0}</div>
              <div className="text-sm text-gray-500">Total Submissions</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Weekly Report Banner */}
      {weeklyReport && (
        <Card className="mb-6 bg-gradient-to-r from-teal-50 to-blue-50 border-teal-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-teal-800">📅 Weekly Syndication Report</h3>
                <p className="text-teal-700 text-sm mt-1">{weeklyReport.recommendation}</p>
              </div>
              <Badge className="bg-teal-600 text-white">
                {weeklyReport.content_to_syndicate?.length || 0} pieces ready
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="content">Content ({content.length})</TabsTrigger>
          <TabsTrigger value="targets">Targets ({targets.length})</TabsTrigger>
          <TabsTrigger value="logs">Activity Log</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Content to Syndicate This Week */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">📝 Content Ready for Syndication</CardTitle>
              </CardHeader>
              <CardContent>
                {content.filter(c => c.syndication_status === 'pending').length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No pending content. Click "Generate Content" to create some!</p>
                ) : (
                  <div className="space-y-3">
                    {content.filter(c => c.syndication_status === 'pending').slice(0, 5).map(item => (
                      <div key={item.id} className="p-3 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-sm text-gray-800 truncate">{item.title}</h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.excerpt}</p>
                        <div className="flex gap-1 mt-2">
                          {item.keywords?.slice(0, 2).map((kw, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Targets Available */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">🎯 Available Syndication Targets</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {targets.filter(t => t.status === 'active').slice(0, 8).map(target => (
                    <div key={target.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <Badge className={getTypeColor(target.type)}>{target.type}</Badge>
                        <span className="text-sm font-medium">{target.name}</span>
                      </div>
                      <a 
                        href={target.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-teal-600 text-xs hover:underline"
                      >
                        Visit →
                      </a>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Instructions */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">📋 How to Use Content Syndication</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">1. Generate Content</h4>
                  <p className="text-gray-600">Click "Generate Content" to create SEO-optimized summaries of your services and blog posts.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">2. Submit to Directories</h4>
                  <p className="text-gray-600">Visit each target site and submit your content. Copy the title, excerpt, and backlink URL.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">3. Log Submissions</h4>
                  <p className="text-gray-600">Mark submissions as complete to track your backlink progress and avoid duplicates.</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  <strong>💡 Pro Tip:</strong> Aim to submit to 3-5 directories per week. Focus on dental and healthcare directories first, then expand to general business directories.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Content Tab */}
        <TabsContent value="content">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Syndicated To</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {content.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm text-gray-800 truncate max-w-xs">{item.title}</div>
                      <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 hover:underline">
                        {item.source_url}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{item.content_type}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={item.syndication_status === 'syndicated' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {item.syndication_status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {item.syndicated_to?.length || 0} targets
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(`${item.title}\n\n${item.excerpt}\n\nRead more: ${item.source_url}`);
                          alert('Content copied to clipboard!');
                        }}
                      >
                        📋 Copy
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Targets Tab */}
        <TabsContent value="targets">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Directory</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submissions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Used</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {targets.map(target => (
                  <tr key={target.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm text-gray-800">{target.name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={getTypeColor(target.type)}>{target.type}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{target.category}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{target.submission_count}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {target.last_submitted ? new Date(target.last_submitted).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <a 
                        href={target.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:underline text-sm mr-2"
                      >
                        Visit Site →
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {logs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No syndication activity yet. Start submitting content to directories!
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Response</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {logs.map(log => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(log.submitted_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 font-medium text-sm text-gray-800">{log.target_name}</td>
                      <td className="px-4 py-3">
                        <Badge className={log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {log.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{log.response || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManageSyndication;
